import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { Text } from '@mxenabled/mxui'

import { Select, useGlobalCopyStore } from '../../../common'
import { DurationUnit, ContributeFrequency } from '../../../core/types/Finstrong'

import {
  calculateContribution,
  getInitialContributionPeriod,
  getInitialTargetPeriodAmount,
} from '../../util/finstrongUtils'

import TotalCard from '../shared/TotalCard'
import { formatCurrency } from '../../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../../core/utils/Localization'

const EstimatedContributionCalculator = ({ total }: { total: number }) => {
  const { finstrong: copy } = useGlobalCopyStore()

  const frequencies = [
    { id: ContributeFrequency.Monthly, label: copy.maintain_emergency_savings_monthly },
    { id: ContributeFrequency.BiWeekly, label: copy.maintain_emergency_savings_bi_weekly },
    { id: ContributeFrequency.Weekly, label: copy.maintain_emergency_savings_weekly },
    { id: ContributeFrequency.Daily, label: copy.maintain_emergency_savings_daily },
  ]

  const frequencyPeriodLabels = {
    [ContributeFrequency.Monthly]: copy.maintain_emergency_savings_estimated_period_monthly,
    [ContributeFrequency.BiWeekly]: copy.maintain_emergency_savings_estimated_period_bi_weekly,
    [ContributeFrequency.Weekly]: copy.maintain_emergency_savings_estimated_period_weekly,
    [ContributeFrequency.Daily]: copy.maintain_emergency_savings_estimated_period_daily,
  }

  const series = [
    { id: DurationUnit.Months, label: copy.maintain_emergency_savings_months },
    { id: DurationUnit.Years, label: copy.maintain_emergency_savings_years },
    { id: DurationUnit.Days, label: copy.maintain_emergency_savings_days },
  ]

  const initialGoalAmount = Math.ceil((total + 1) / 1000) * 1000

  const [calculatedContribution, setCalculatedContribution] = React.useState<number>(0)
  const [calculatedContributionPerPeriod, setCalculatedContributionPerPeriod] =
    React.useState<ContributeFrequency>(getInitialContributionPeriod(total))
  const [contribution, setContribution] = React.useState<ContributeFrequency>(
    getInitialContributionPeriod(total),
  )
  const [goalAmount, setGoalAmount] = React.useState<number>(initialGoalAmount)
  const [goalPeriod, setGoalPeriod] = React.useState<number>(getInitialTargetPeriodAmount(total))
  const [targetPeriod, setTargetPeriod] = React.useState<DurationUnit>(DurationUnit.Months)

  React.useEffect(() => {
    setCalculatedContribution(
      calculateContribution({
        contribution,
        goalAmount,
        goalPeriod,
        targetPeriod,
        totalSaved: total,
      }),
    )
    setCalculatedContributionPerPeriod(contribution)
  }, [])

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault()

    setCalculatedContribution(
      calculateContribution({
        contribution,
        goalAmount,
        goalPeriod,
        targetPeriod,
        totalSaved: total,
      }),
    )
    setCalculatedContributionPerPeriod(contribution)
  }

  return (
    <Card>
      <form onSubmit={handleCalculate}>
        <Stack sx={{ gap: 24, pb: 24, pt: 16, px: 16 }}>
          <TotalCard
            balance={
              <Stack sx={{ flexDirection: 'row', height: 32 }}>
                <Text variant="h2">{formatCurrency(calculatedContribution, '0,0')}</Text>
                <Text sx={{ alignSelf: 'end', height: 24 }} variant="subtitle2">
                  /{frequencyPeriodLabels[calculatedContributionPerPeriod]}
                </Text>
              </Stack>
            }
            title={copy.maintain_emergency_savings_estimated_contribution}
          />

          <TextField
            aria-label={copy.maintain_emergency_savings_savings_goal}
            helperText={
              buildDynamicCopy(
                copy.maintain_emergency_savings_suggested_goal,
                formatCurrency(initialGoalAmount, '0,0'),
              ) as string
            }
            label={copy.maintain_emergency_savings_savings_goal}
            onChange={(e) => {
              if (e.target.value) setGoalAmount(Number(e.target.value.replace(/[^0-9]/g, '')))
            }}
            slotProps={{
              htmlInput: {
                noValidate: true,
                pattern: '[0-9]*',
              },
            }}
            value={goalAmount ? formatCurrency(goalAmount, '0,0') : ''}
          />
          <Select
            label={copy.maintain_emergency_savings_how_often_contribute}
            onChange={setContribution}
            options={frequencies}
            required={true}
            value={contribution}
          />
          <Stack sx={{ flexDirection: 'row' }}>
            <TextField
              aria-label={copy.maintain_emergency_savings_meet_your_goal_within}
              fullWidth={true}
              label={copy.maintain_emergency_savings_meet_your_goal_within}
              onChange={(e) => {
                if (!isNaN(Number(e.target.value))) setGoalPeriod(Number(e.target.value))
              }}
              slotProps={{
                htmlInput: {
                  noValidate: true,
                  pattern: '[0-9]*',
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderBottomRightRadius: 0,
                  borderTopRightRadius: 0,
                  fieldset: {
                    borderRightWidth: 0.5,
                  },
                },
              }}
              value={goalPeriod}
            />
            <Box
              sx={{
                '& .MuiInputBase-root': {
                  borderBottomLeftRadius: 0,
                  borderTopLeftRadius: 0,
                  fieldset: {
                    borderLeftWidth: 0.5,
                  },
                },
              }}
            >
              <Select label="" onChange={setTargetPeriod} options={series} value={targetPeriod} />
            </Box>
          </Stack>
          <Button fullWidth={true} onClick={handleCalculate} type="submit" variant="contained">
            {copy.maintain_emergency_savings_calculate}
          </Button>
        </Stack>
      </form>
    </Card>
  )
}

export default observer(EstimatedContributionCalculator)
